import { useEffect, useState } from "react";
import {
  adminDeactivate,
  SearchAdminByParams,
  sendAdminInvite,
  sendAdminInviteCoach,
  sendAdminInviteHighSchoolCoach,
  sendAdminInviteClubCoach,
  getHighSchoolContactInfo,
  createHighSchoolContactInfo,
} from "../../../services/AdminService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import {
  fetchHighSchool,
  fetchClubs,
} from "../../../services/LeaderboardService";
import { useDispatch, useSelector } from "react-redux";
import {
  adminSearchAction,
  removeAdminUserFromList,
  resetAdminState,
} from "../../../store/slices/adminSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faTimes } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ConfirmModal from "../../../components/ConfirmModel";
import DropdownWithSearch from "../../../components/common/DropdownWithSearch";
import "react-toastify/dist/ReactToastify.css";
import GraduatedClass from "./GraduatedClass";
import CoachContactModal, {
  HighSchoolCoachInfoCard,
} from "./CoachContactModal";

const getSchoolKey = (school) => {
  if (!school) return "";
  if (typeof school === "string") return school;
  return school.value || school.label || "";
};

const isSelectionValid = (items) =>
  items.length > 0 &&
  items.some((item) =>
    typeof item === "string"
      ? item.trim() !== ""
      : Boolean(item?.value || item?.label || item),
  );

export const LeftSide = () => {
  const [tab, setTab] = useState(1);
  const [loading, setLoading] = useState(null);
  const [loadingInvited, setLoadingInvited] = useState(false);
  const [email, setEmail] = useState("");
  const [coachEmail, setCoachEmail] = useState("");
  const [coachType, setCoachType] = useState("college_coach");
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [schoolError, setSchoolError] = useState("");
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [selectedClub, setSelectedClub] = useState([]);
  const [clubError, setClubError] = useState("");
  const [clubOptions, setClubOptions] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [listAdmin, setListAdmin] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [errorSearch, setErrorSearch] = useState("");
  const [isCoachContactModalOpen, setIsCoachContactModalOpen] = useState(false);
  const [selectedCoachSchool, setSelectedCoachSchool] = useState(null);
  const [coachContactsBySchool, setCoachContactsBySchool] = useState({});

  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const r_admin = useSelector((state) => state.admin);
  const { adminUsers } = r_admin;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendAdminInvite(apiInstance, email);
      const newAdmin = { email: email };
      setListAdmin((prevList) => [...prevList, newAdmin]);
      dispatch(adminSearchAction([...adminUsers, newAdmin]));
      setEmail("");
      toast.success("Invitation sent successfully!");
    } catch (err) {
      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err.message ||
        "An unknown error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCoachInvite = async (e) => {
    e.preventDefault();

    if (
      coachType === "high_school_coach" &&
      !isSelectionValid(selectedSchools)
    ) {
      setSchoolError("Please select a school.");
      return;
    }

    if (coachType === "club_coach" && !isSelectionValid(selectedClub)) {
      setClubError("Please select a club.");
      return;
    }

    setSchoolError("");
    setClubError("");
    setLoadingInvited(true);
    try {
      if (coachType === "high_school_coach") {
        await sendAdminInviteHighSchoolCoach(
          apiInstance,
          coachEmail,
          selectedSchools[0],
        );
        setSelectedSchools([]);
      } else if (coachType === "club_coach") {
        await sendAdminInviteClubCoach(
          apiInstance,
          coachEmail,
          selectedClub[0],
        );
        setSelectedClub([]);
      } else {
        await sendAdminInviteCoach(apiInstance, coachEmail);
      }
      setCoachEmail("");
      toast.success("Coach invitation sent successfully!");
    } catch (err) {
      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err.message ||
        "An unknown error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoadingInvited(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    dispatch(resetAdminState());
  };

  const handleCoachEmailChange = (e) => {
    setCoachEmail(e.target.value);
  };

  const handleSchoolSelect = (schools) => {
    setSelectedSchools(schools);
    if (schools.length > 0) {
      setSchoolError("");
    }
  };

  const handleClubSelect = (clubs) => {
    setSelectedClub(clubs);
    if (clubs.length > 0) {
      setClubError("");
    }
  };

  const handleSearchAdmin = async (e) => {
    const value = e.target.value;
    if (value) {
      const result = adminUsers.filter((item) =>
        (item.email ?? "").includes(value),
      );
      setListAdmin(result);
    } else {
      setListAdmin(adminUsers);
    }
  };

  const deactivateAdmin = (userId) => {
    setSelectedUserId(userId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUserId) {
      setLoading(true);
      try {
        await adminDeactivate(apiInstance, selectedUserId);
        dispatch(removeAdminUserFromList(selectedUserId));
        toast.success("Admin deactivated successfully!");
      } catch (err) {
        toast.error("Failed to deactivate admin.");
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
        setSelectedUserId(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedUserId(null);
  };

  const handleOpenCoachContactModal = () => {
    setSelectedCoachSchool(null);
    setIsCoachContactModalOpen(true);
  };

  const handleCloseCoachContactModal = () => {
    setIsCoachContactModalOpen(false);
  };

  const handleCoachContactUpdate = async ({ schoolKey, data }) => {
    try {
      await createHighSchoolContactInfo(
        apiInstance,
        schoolKey.trim(),
        data.firstName,
        data.lastName,
        data.phoneNumber,
      );
      setCoachContactsBySchool((prev) => ({
        ...prev,
        [schoolKey]: data,
      }));
      setSelectedCoachSchool(
        schoolOptions.find((school) => getSchoolKey(school) === schoolKey) ||
          selectedCoachSchool,
      );
    } catch (err) {
      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to save coach contact.";
      toast.error(errorMessage);
      throw err;
    }
  };

  useEffect(() => {
    if (adminUsers) {
      setListAdmin(adminUsers);
    }
  }, [adminUsers]);

  useEffect(() => {
    const loadAdminUsers = async () => {
      setLoadingSearch(true);
      try {
        const data = await SearchAdminByParams(apiInstance, "");
        dispatch(adminSearchAction(data));
      } catch (err) {
        const errorMessage = err.message || "An unknown error occurred.";
        setErrorSearch(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoadingSearch(false);
      }
    };
    loadAdminUsers();
  }, [dispatch, apiInstance]);

  useEffect(() => {
    const loadSchoolOptions = async () => {
      setLoadingSchools(true);
      try {
        const schools = await fetchHighSchool(apiInstance);
        setSchoolOptions(Array.isArray(schools) ? schools : []);
      } catch (err) {
        const errorMessage = err.message || "Error loading schools.";
        toast.error(errorMessage);
      } finally {
        setLoadingSchools(false);
      }
    };
    loadSchoolOptions();
  }, [apiInstance]);

  useEffect(() => {
    const loadClubOptions = async () => {
      setLoadingClubs(true);
      try {
        const clubs = await fetchClubs(apiInstance);
        setClubOptions(Array.isArray(clubs) ? clubs : []);
      } catch (err) {
        const errorMessage = err.message || "Error loading clubs.";
        toast.error(errorMessage);
      } finally {
        setLoadingClubs(false);
      }
    };
    loadClubOptions();
  }, [apiInstance]);

  useEffect(() => {
    const schoolKey = getSchoolKey(selectedCoachSchool);
    if (!schoolKey || coachContactsBySchool[schoolKey]) return;

    const loadCoachContact = async () => {
      try {
        const contact = await getHighSchoolContactInfo(apiInstance, schoolKey);
        setCoachContactsBySchool((prev) => ({
          ...prev,
          [schoolKey]: {
            firstName: contact?.firstName || "",
            lastName: contact?.lastName || "",
            phoneNumber: contact?.phoneNumber || "",
          },
        }));
      } catch (err) {
        const errorMessage =
          err.message || "Error loading high school contact.";
        toast.error(errorMessage);
        setCoachContactsBySchool((prev) => ({
          ...prev,
          [schoolKey]: {
            firstName: "",
            lastName: "",
            phoneNumber: "",
          },
        }));
      }
    };
    loadCoachContact();
  }, [apiInstance, selectedCoachSchool, coachContactsBySchool]);

  const style = {
    ul: "flex space-x-0 mt-0 border-b border-gray-300",
    li: "text-gray-900 pb-2 px-3 hover:opacity-70 text-sm xl:text-base cursor-pointer",
    selected:
      "cursor-default border-b-4 border-blue-700 text-sm xl:text-base text-blue-700 pb-2 px-3 font-medium ",
  };

  const isSchoolSelectionValid = isSelectionValid(selectedSchools);
  const isClubSelectionValid = isSelectionValid(selectedClub);

  const isInviteDisabled =
    (coachType === "high_school_coach" && !isSchoolSelectionValid) ||
    (coachType === "club_coach" && !isClubSelectionValid);

  return (
    <>
      <div className="bg-white shadow-lg py-4 rounded-lg border-gray-300">
        <div className="w-full px-4">
          <h1 className="text-xl mb-4 uppercase">Users</h1>
          <div className="top-0 bg-white z-20 mb-4">
            <ul className={style.ul}>
              <li
                className={tab === 1 ? style.selected : style.li}
                onClick={() => setTab(1)}
              >
                Admin
              </li>
              <li
                className={tab === 2 ? style.selected : style.li}
                onClick={() => setTab(2)}
              >
                Coach
              </li>
            </ul>
          </div>

          {tab === 1 && (
            <>
              <form
                className="mt-4 space-y-4 lg:mt-5 md:space-y-5"
                onSubmit={handleInvite}
              >
                <div className="w-full">
                  <label
                    htmlFor="adminEmail"
                    className="block mb-1 text-sm font-medium text-gray-700"
                  >
                    Admin Email Address
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="email"
                      name="email"
                      required
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="newadmin@trackit.com"
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-300 
               text-gray-700 placeholder-gray-400 
               focus:outline-none focus:ring-2 focus:ring-indigo-400 
               focus:border-indigo-400 transition"
                    />{" "}
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <button
                        type="submit"
                        className="px-6 py-2.5 rounded text-sm
               bg-red-500 text-white cursor-pointer
               hover:bg-red-600 
               active:scale-95 
               transition-all duration-200"
                      >
                        Invite Admin
                      </button>
                    )}
                  </div>
                </div>
              </form>

              <div className="flex flex-row items-center justify-between gap-3 pt-8">
                <div className=" font-semibold"> Admin Users</div>
                <div className="flex px-3 py-2 rounded-md gap-2 border-0 ring-1 ring-inset ring-gray-300 overflow-hidden placeholder:text-gray-600">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="text-gray-600 text-sm relative top-1"
                  />
                  <input
                    type="text"
                    onChange={handleSearchAdmin}
                    placeholder="Search"
                    className="w-full outline-none bg-transparent text-gray-600 text-sm"
                  />
                </div>
              </div>

              {loadingSearch && <LoadingSpinner />}
              {errorSearch && (
                <p className="text-red-500">Error: {errorSearch}</p>
              )}
              {!loadingSearch && (
                <div className="w-full">
                  {listAdmin && listAdmin.length > 0 ? (
                    <div className="overflow-y-auto pr-4 -mr-4 h-[300px] mt-2">
                      <div className="mt-2 border border-gray-300 rounded-md overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200 hidden">
                              <th className="text-left py-2 px-3 bg-gray-100 md:table-cell">
                                Email
                              </th>
                              <th className="text-left py-2 px-3 bg-gray-100">
                                ...
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {listAdmin.map((admin) => (
                              <tr
                                key={admin.id}
                                className="border-b border-gray-200 hover:bg-blue-50 transition-colors duration-150"
                              >
                                <td className="py-2 px-3 text-base">
                                  {admin.email}
                                </td>

                                <td className="py-2 px-3 text-right">
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className="h-4 text-red-500 hover:text-red-700 cursor-pointer"
                                    onClick={() => deactivateAdmin(admin.id)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="col-span-3 text-gray-500">
                      No results found.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {tab === 2 && (
            <form
              className="mt-4 space-y-4 lg:mt-5 md:space-y-5"
              onSubmit={handleCoachInvite}
            >
              <div className="w-full">
                <p className="block mb-1 text-sm font-medium text-gray-700">
                  Coach Type
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                  <label className="inline-flex items-center gap-2 text-gray-700">
                    <input
                      type="radio"
                      name="coachType"
                      value="college_coach"
                      checked={coachType === "college_coach"}
                      onChange={(e) => {
                        setCoachType(e.target.value);
                        setSchoolError("");
                        setClubError("");
                      }}
                    />
                    College Coach
                  </label>
                  <label className="inline-flex items-center gap-2 text-gray-700">
                    <input
                      type="radio"
                      name="coachType"
                      value="high_school_coach"
                      checked={coachType === "high_school_coach"}
                      onChange={(e) => {
                        setCoachType(e.target.value);
                        setSchoolError("");
                        setClubError("");
                      }}
                    />
                    High School Coaches
                  </label>
                  <label className="inline-flex items-center gap-2 text-gray-700">
                    <input
                      type="radio"
                      name="coachType"
                      value="club_coach"
                      checked={coachType === "club_coach"}
                      onChange={(e) => {
                        setCoachType(e.target.value);
                        setSchoolError("");
                        setClubError("");
                      }}
                    />
                    Club Coach
                  </label>
                </div>
              </div>

              {coachType === "high_school_coach" && (
                <DropdownWithSearch
                  label="Select School"
                  options={schoolOptions}
                  selectedValues={selectedSchools}
                  onSelect={handleSchoolSelect}
                  allowMultiple={false}
                  isLoading={loadingSchools}
                  containerClassName="relative w-full"
                  buttonClassName={`w-full flex justify-between items-center border px-4 py-2 rounded-lg bg-white ${
                    schoolError ? "border-red-500" : ""
                  }`}
                />
              )}
              {coachType === "high_school_coach" && schoolError && (
                <p className="text-sm text-red-500 -mt-2">{schoolError}</p>
              )}

              {coachType === "club_coach" && (
                <div>
                  <DropdownWithSearch
                    label="Select Club"
                    options={clubOptions}
                    selectedValues={selectedClub}
                    onSelect={handleClubSelect}
                    allowMultiple={false}
                    isLoading={loadingClubs}
                    containerClassName="relative w-full"
                    buttonClassName={`w-full flex justify-between items-center border px-4 py-2 rounded-lg bg-white ${
                      clubError ? "border-red-500" : ""
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    To add a club to this dropdown, update the club field on an
                    athlete profile.
                  </p>
                </div>
              )}
              {coachType === "club_coach" && clubError && (
                <p className="text-sm text-red-500 -mt-2">{clubError}</p>
              )}

              <div className="w-full">
                <label
                  htmlFor="adminEmail"
                  className="block mb-1 text-sm font-medium text-gray-700"
                >
                  Coach Email Address
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="email"
                    name="coachEmail"
                    required
                    value={coachEmail}
                    onChange={handleCoachEmailChange}
                    placeholder="coach@trackit.com"
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 
               text-gray-700 placeholder-gray-400 
               focus:outline-none focus:ring-2 focus:ring-indigo-400 
               focus:border-indigo-400 transition"
                  />{" "}
                  {loadingInvited ? (
                    <LoadingSpinner />
                  ) : (
                    <button
                      type="submit"
                      disabled={isInviteDisabled}
                      className="px-6 py-2.5 rounded text-sm
               bg-red-500 text-white cursor-pointer
               hover:bg-red-600 
               active:scale-95 
               transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-500 disabled:active:scale-100"
                    >
                      Invite Coach
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      <HighSchoolCoachInfoCard
        handleOpenCoachContactModal={handleOpenCoachContactModal}
      />
      <div className="bg-white shadow-lg py-4 rounded-lg border-gray-300 mt-4">
        <div className="w-full px-4">
          {/* Graduated Class Setting */}
          <GraduatedClass />
        </div>
      </div>

      <ConfirmModal
        show={showDeleteModal}
        message="Are you sure you want to delete this administrator?"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <CoachContactModal
        isOpen={isCoachContactModalOpen}
        onClose={handleCloseCoachContactModal}
        schoolOptions={schoolOptions}
        selectedSchool={selectedCoachSchool}
        onSchoolChange={setSelectedCoachSchool}
        coachContacts={coachContactsBySchool}
        onUpdate={handleCoachContactUpdate}
        isLoadingSchools={loadingSchools}
      />
    </>
  );
};
